import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { createActionType } from './utils'
import { metadataClient } from '../clients/MetadataClient'
import {
    ChassisTypeDirectory,
    ColorDirectory,
    VehicleTypeDirectory,
    LoadTypeDirectory,
} from '../models/client/metadata'

const REDUCER_NAME = 'metadata'

const createMetadataActionType = (name: string): string => createActionType(name, REDUCER_NAME)

type MetadataState = Partial<{
    colors: ColorDirectory
    vehicleTypes: VehicleTypeDirectory
    loadTypes: LoadTypeDirectory
    chassisTypes: ChassisTypeDirectory
}>

export const loadMetadataAction = createAsyncThunk(
    createMetadataActionType('loadMetadata'),
    async ({ token }: { token: string }): Promise<MetadataState> => {
        const colors = await metadataClient.getColors({ token })
        const vehicleTypes = await metadataClient.getVehicleTypes({ token })
        const loadTypes = await metadataClient.getLoadTypes({ token })
        const chassisTypes = await metadataClient.getChassisTypes({ token })
        return {
            colors,
            vehicleTypes,
            loadTypes,
            chassisTypes,
        }
    }
)

const initialState: MetadataState = {
    colors: undefined,
    vehicleTypes: undefined,
    loadTypes: undefined,
    chassisTypes: undefined,
}

const slice = createSlice({
    name: REDUCER_NAME,
    reducers: {
        clearMetadata(draft) {
            draft.colors = undefined
            draft.loadTypes = undefined
            draft.chassisTypes = undefined
            draft.vehicleTypes = undefined
        },
    },
    initialState,
    extraReducers(builder) {
        builder.addCase(loadMetadataAction.fulfilled, (draft, { payload }) => {
            draft.colors = payload.colors
            draft.loadTypes = payload.loadTypes
            draft.chassisTypes = payload.chassisTypes
            draft.vehicleTypes = payload.vehicleTypes
        })
    },
})

export const { reducer } = slice
export const { clearMetadata } = slice.actions

type State = {
    metadata: MetadataState
}

export const selectMetadataEntities = (state: State): MetadataState => state.metadata

export const selectColorMetadata = (state: State): ColorDirectory | undefined =>
    state.metadata.colors

export const selectLoadTypeMetadata = (state: State): LoadTypeDirectory | undefined =>
    state.metadata.loadTypes

export const selectChassisTypeMetadata = (state: State): ChassisTypeDirectory | undefined =>
    state.metadata.chassisTypes

export const selectVehicleTypeMetadata = (state: State): VehicleTypeDirectory | undefined =>
    state.metadata.vehicleTypes
